import type { VersionedWording } from '@/lib/versioned-labels';
import { Plus } from 'lucide-react';
import type { ReactNode } from 'react';

/**
 * « Créer un nouveau critère » - the dashed card, in FIRST position (refonte
 * modulaire §4, issue #113).
 *
 * ## There is no creation box, and that is the decision
 *
 * §6: « pas de boîte. La carte en pointillé ouvre l'éditeur vide ; le premier
 * "Enregistrer une nouvelle version" écrit l'entité et la v1 dans la même
 * transaction. » So this card is a LINK and not a button - it navigates, it
 * asks nothing, and nothing exists behind the address it opens until the first
 * save. A dialog here would have collected a name and a direction twice: once
 * to create, once to edit.
 *
 * ## And no « Créer » in the page head either
 *
 * The card is the only door, and it sits at the START of the grid. It sat at
 * the end until issue #113 (2026-09-21), on the argument that creating is what
 * an operator does after failing to find what they came for. Nicolas reversed
 * it: on a library of twenty cards that door was a scroll away, and a door whose
 * place moves with the length of the list is one to hunt for every time. First,
 * it sits at the same place whatever the list holds - filtered, empty or full.
 *
 * The link itself is built by the caller, for the reason `versioned-card.tsx`
 * gives at its own head: TanStack Router types a path against the route tree,
 * and a path handed down through a generic component stops type-checking.
 */

export interface CreateCardProps {
  wording: VersionedWording;
  /** The link to the empty editor - the caller builds it, the layer dresses it. */
  renderLink: (props: {
    className: string;
    'data-testid': string;
    children: ReactNode;
  }) => ReactNode;
}

const CARD_CLASS =
  'flex min-h-43 flex-col items-center justify-center gap-3 rounded-md border-2 border-gray-300 border-dashed p-5 text-bleu-nuit no-underline hover:border-bleu-nuit hover:bg-bleu-nuit-tint';

export function CreateCard({ wording, renderLink }: CreateCardProps) {
  return renderLink({
    className: CARD_CLASS,
    'data-testid': `${wording.entity}-carte-creation`,
    children: (
      <>
        <span className="inline-flex size-11 items-center justify-center rounded-pill border-2 border-bleu-nuit">
          <Plus aria-hidden="true" className="size-5.5" strokeWidth={2.5} />
        </span>
        <span className="font-semibold text-base leading-6">{wording.createLabel}</span>
      </>
    ),
  });
}
